import { useEffect, useState } from 'react';
import axios from 'axios';
import { getPetById } from '../api/petApi';
import { adaptPet } from '../api/petAdapter';
import type { Pet } from '../types';

export interface UsePetResult {
  pet: Pet | null;
  loading: boolean;
  error: Error | null;
  notFound: boolean;
}

/**
 * Fetches a single pet by id. Distinguishes 404 (notFound=true) from
 * other errors so the detail page can render the editorial "ficha não
 * encontrada" state without spilling stack traces.
 */
export function usePet(id: string | undefined): UsePetResult {
  const [state, setState] = useState<UsePetResult>({
    pet: null,
    loading: !!id,
    error: null,
    notFound: false,
  });

  useEffect(() => {
    if (!id) {
      setState({ pet: null, loading: false, error: null, notFound: false });
      return;
    }

    const ctrl = new AbortController();
    let cancelled = false;
    setState({ pet: null, loading: true, error: null, notFound: false });

    getPetById(id, ctrl.signal)
      .then((be) => {
        if (cancelled) return;
        setState({ pet: adaptPet(be), loading: false, error: null, notFound: false });
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err)) return;
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_CANCELED') return;
          if (err.response?.status === 404) {
            setState({ pet: null, loading: false, error: null, notFound: true });
            return;
          }
        }
        setState({
          pet: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
          notFound: false,
        });
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [id]);

  return state;
}
